import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { type UserRegisterData } from '../types/auth'
import { useRegisterOrganizationMutation, useRegisterUserMutation } from '../store/api/authApi'
import { type OrganizationCreationData } from '../types'

export function RegisterPage (): JSX.Element {
  const navigate = useNavigate()

  const [registerOrganization] = useRegisterOrganizationMutation()
  const [registerUser] = useRegisterUserMutation()

  const [step, setStep] = useState<'personal' | 'organization' | 'apiKey'>('personal')

  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')

  const [organizationName, setOrganizationName] = useState<string>('')
  const [organizationContactName, setOrganizationContactName] = useState<string>('')
  const [organizationContactEmail, setOrganizationContactEmail] = useState<string>('')
  const [organizationPhoneNumber, setOrganizationPhoneNumber] = useState<string>('')
  const [organizationWebsite, setOrganizationWebsite] = useState<string>('')
  const [organizationAddress, setOrganizationAddress] = useState<string>('')

  const [password, setPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')

  const [showError, setShowError] = useState<boolean>(false)
  const [showCopiedLabel, setShowCopiedLabel] = useState<boolean>(false)

  const [apiKey, setApiKey] = useState<string>('')

  const onRegisterClicked = useCallback(async () => {
    try {
      const organizationCreateRequest: OrganizationCreationData = {
        name: organizationName,
        contact_name: organizationContactName,
        contact_email: organizationContactEmail,
        phone_number: organizationPhoneNumber,
        website: organizationWebsite,
        address: organizationAddress
      }

      const organization = await registerOrganization({ data: organizationCreateRequest }).unwrap()

      if (organization) {
        const registerUserRequest: UserRegisterData = {
          firstname: firstName,
          lastname: lastName,
          email: organizationContactEmail,
          phone_number: organizationPhoneNumber,
          organization_id: organization.id,
          password
        }

        await registerUser({ data: registerUserRequest }).unwrap()
        setStep('apiKey')
        setApiKey(organization.api_key)
        setShowError(false)
      }
    } catch (e) {
      setShowError(true)
    }
  }, [
    organizationName,
    organizationContactName,
    organizationContactEmail,
    organizationPhoneNumber,
    organizationWebsite,
    organizationAddress
  ])

  const isEmailValid = useCallback(() => {
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(organizationContactEmail)
  }, [organizationContactEmail])

  const isPasswordValid = useCallback(() => {
    return /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&]{8,}$/.test(password)
  }, [password])

  const isFormValid = useCallback(() => {
    if (step === 'personal') {
      if (!firstName || !lastName || !organizationContactEmail || !organizationPhoneNumber || !password || !confirmPassword || password !== confirmPassword) {
        return false
      }

      if (!isEmailValid()) {
        return false
      }

      if (!isPasswordValid()) {
        return false
      }

      return true
    }

    if (!organizationName || !organizationContactName || !organizationWebsite || !organizationAddress) {
      return false
    }

    return true
  }, [
    step,
    firstName,
    lastName,
    organizationName,
    organizationContactName,
    organizationContactEmail,
    organizationPhoneNumber,
    organizationWebsite,
    organizationAddress,
    password,
    confirmPassword,
    isEmailValid,
    isPasswordValid
  ])

  const copyApiKeyToClipboard = async (): Promise<void> => {
    // await navigator.clipboard.writeText('cf9fpYQ04MTlQwT51k0fwtIF0MLJCTauwRSxVfSEYieTvv8KwqY01VKgRzwWaXnexl90AUigDt44P1SIkMJA96UbEnoRpVtJptjW12SBBig2ORKzq3vUd7BlLs37yRBBv')
    await navigator.clipboard.writeText(apiKey)
    setShowCopiedLabel(true)

    setTimeout(() => {
      setShowCopiedLabel(false)
    }, 2000)
  }

  return (
    <div className="page-auth container-fluid">
      <div className="content-row row">
        <h3 className="mb-5 text-center">Classification Engine - Register</h3>
        <div className="row">
          {
            step === 'personal' ? <div className="col col-12">
              <h4 className="mb-3">Personal information</h4>
              <div className="form-group mb-3">
                <label htmlFor="firstName" className="mb-1">First name</label>
                <input id="firstName" name="firstName" value={firstName} onChange={(e) => { setFirstName(e.target.value) }}/>
              </div>
              <div className="form-group mb-3">
                <label htmlFor="lastName" className="mb-1">Last name</label>
                <input id="lastName" name="lastName" value={lastName} onChange={(e) => { setLastName(e.target.value) }}/>
              </div>
              <div className="form-group mb-3">
                <label htmlFor="organizationContactEmail" className="mb-1">Email</label>
                <input id="organizationContactEmail" name="organizationContactEmail" value={organizationContactEmail} onChange={(e) => { setOrganizationContactEmail(e.target.value) }}/>
              </div>
              <div className="form-group mb-3">
                <label htmlFor="organizationPhoneNumber" className="mb-1">Phone Number</label>
                <input id="organizationPhoneNumber" name="organizationPhoneNumber" value={organizationPhoneNumber} onChange={(e) => { setOrganizationPhoneNumber(e.target.value) }}/>
              </div>
              <div className="form-group mb-3">
                <label htmlFor="password" className="mb-1">Password</label>
                <input id="password" name="password" type="password" value={password} onChange={(e) => { setPassword(e.target.value) }}/>
                <i><span>Password must contain at least 8 characters, a lowercase letter, an uppercase letter, a digit and a special symbols (! @ # $ % ^ &)</span></i>
              </div>
              <div className="form-group mb-5">
                <label htmlFor="confirmPassword" className="mb-1">Confirm Password</label>
                <input id="confirmPassword" name="confirmPassword" type="password" value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value) }}/>
              </div>
            </div> : null
          }
          {
            step === 'organization' ? <div className="col col-12">
              <h4 className="mb-3">Organization information</h4>
              <div className="form-group mb-3">
                <label htmlFor="organizationName" className="mb-1">Organization Name</label>
                <input id="organizationName" name="organizationName" value={organizationName} onChange={(e) => { setOrganizationName(e.target.value) }}/>
              </div>
              <div className="form-group mb-3">
                <label htmlFor="organizationContactName" className="mb-1">Organization Contact Name</label>
                <input id="organizationContactName" name="organizationContactName" value={organizationContactName} onChange={(e) => { setOrganizationContactName(e.target.value) }}/>
              </div>
              <div className="form-group mb-3">
                <label htmlFor="organizationAddress" className="mb-1">Organization Address</label>
                <input id="organizationAddress" name="organizationAddress" value={organizationAddress} onChange={(e) => { setOrganizationAddress(e.target.value) }}/>
              </div>
              <div className="form-group mb-3">
                <label htmlFor="organizationWebsite" className="mb-1">Organization Website</label>
                <input id="organizationWebsite" name="organizationWebsite" value={organizationWebsite} onChange={(e) => { setOrganizationWebsite(e.target.value) }}/>
              </div>
            </div> : null
          }
          {
            step === 'apiKey' ? <div className="api-key-wrapper">
              <h5>Your API key has been generated:</h5>
              <button type="button" className="btn btn-primary btn-copy-key" onClick={async () => { await copyApiKeyToClipboard() }}>
                Click To Copy
                {
                  showCopiedLabel ? <span className="copied-label ml-3">Copied</span> : null
                }
              </button>
            </div> : null
          }
        </div>
        {
          showError ? <div className="error text-center mb-5">Failed to register user</div> : null
        }
        {
          step === 'personal' ? <button className="btn-confirm mb-3" onClick={() => { setStep('organization') }} disabled={!isFormValid()}>Next</button> : null
        }
        {
          step === 'organization' ? <button className="btn-confirm mb-3 mt-3" onClick={onRegisterClicked} disabled={!isFormValid()}>Register</button> : null
        }
        {
          step === 'apiKey' ? <button className="btn-confirm mb-3" onClick={() => { navigate('/login') }}>Open Login Page</button> : null
        }
      </div>
    </div>
  )
}
